// Third-party
import { v4 as uuidv4 } from "uuid";
import { isBoolean, isNull, isObject } from "lodash";

// MUI
import { Typography } from "@mui/material";

// Components
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Helpers
import { getItemTitleFromTitle, getPropertiesList } from "@/DataPages/components/common/ListColumnDialog/helpers";

// Types
import { IRow } from "@/DataPages/types/common";
import { IObjectProperty } from "@/DataPages/types/common/ListColumnDialog";

const renderRowForText = (row: IObjectProperty) => {
	if (isNull(row?.value) || row?.value === undefined) {
		return `${row.displayName}:`;
	}

	const value = isBoolean(row.value) ? (row.value ? "Y" : "N") : (row.value as string);

	return (
		<TypographyCell hasTooltip={false} className={"!flex !row !gap-1 !pt-0 !pl-1 !pb-1 !items-center"}>
			<Typography className="!font-sans !text-xs !font-bold !shrink-0">{`${row.displayName}:`}</Typography>
			<TypographyCell
				className="!font-sans !text-xs !break-words !pt-0 !w-40"
				hasTooltip
				tooltipProps={{ title: `${value}` }}
			>{`${value}`}</TypographyCell>
		</TypographyCell>
	);
};

const renderRowForObject = (row: IObjectProperty) => {
	const isEmptyArray = row.dataType === "list" && !row.value?.length;
	const isEmptyObject = row.dataType === "object" && !row.value;
	return (
		<TypographyCell className="!font-bold !pt-0 !pl-1">
			{isEmptyArray || isEmptyObject ? `${row.displayName}:` : row.displayName}
		</TypographyCell>
	);
};

const generateTreeData = (properties: IObjectProperty[], path: string[] = []) => {
	if (!properties?.length) {
		return [];
	}
	const rowsMapped: IRow[] = [];

	for (const propertyItem of properties) {
		const newId = uuidv4();
		const labelValue =
			propertyItem.dataType === "list" || propertyItem.dataType === "object"
				? renderRowForObject(propertyItem)
				: renderRowForText(propertyItem);
		const newRow: IRow = {
			_id: newId,
			label: labelValue,
			path: [...path, newId],
		};
		rowsMapped.push(newRow);
		if (propertyItem.dataType === "list") {
			const itemTitle =
				typeof propertyItem.displayName === "string" ? getItemTitleFromTitle(propertyItem.displayName) : propertyItem.displayName;
			const valueList = (propertyItem.value as object[]).map((valueItem, index) => {
				return {
					displayName: `${itemTitle} ${index + 1}`,
					dataType: "object",
					value: getPropertiesList(valueItem),
				};
			});

			let children: IRow[] = [];
			valueList.forEach((valueItem) => {
				const nestedNewId = uuidv4();
				const nestedNewRow: IRow = {
					_id: `${newId}-${nestedNewId}`,
					label: valueItem.displayName,
					path: [...path, newId, nestedNewId],
				};
				rowsMapped.push(nestedNewRow);
				children = generateTreeData(valueItem.value, nestedNewRow.path);
				rowsMapped.push(...children);
			});
		}
		if (propertyItem.dataType === "object" && isObject(propertyItem.value)) {
			const propertiesList = getPropertiesList(propertyItem.value as object);
			const children = generateTreeData(propertiesList, newRow.path);
			rowsMapped.push(...children);
		}
	}
	return rowsMapped;
};

export { getPropertiesList, generateTreeData };
